import React, { useState, useEffect, useRef } from 'react';
import { formatDate } from '@/lib/dateUtils';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  FileText, Upload, CheckCircle2, AlertCircle, Calendar,
  Building2, DollarSign, Loader2, X, Plus, Clock,
  FileCheck, Send, Eye, Download, ChevronDown, ChevronRight,
  Shield, ClipboardCheck, PenTool, LayoutDashboard, RefreshCw,
  MessageSquare, Gavel, Lock, User, LogOut, HelpCircle, Camera, Menu
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';

function ContractorPortalV2() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [isLoading, setIsLoading] = useState(true);
  const [portalData, setPortalData] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  // Mobile nav drawer (hamburger) — sidebar becomes a slide-in drawer below lg
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => { setMobileNavOpen(false); }, [activeTab]);
  
  // Invoice submission state
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    project_id: '',
    phase_id: '',
    amount: '',
    description: '',
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0]
  });
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [isSubmittingInvoice, setIsSubmittingInvoice] = useState(false);
  
  // Resubmit invoice state
  const [showResubmitModal, setShowResubmitModal] = useState(false);
  const [resubmittingInvoice, setResubmittingInvoice] = useState(null);
  const [resubmitForm, setResubmitForm] = useState({
    amount: '',
    description: '',
    invoice_number: '',
    invoice_date: ''
  });
  const [resubmitFile, setResubmitFile] = useState(null);
  const [isResubmitting, setIsResubmitting] = useState(false);
  
  // Document upload state
  const [w9File, setW9File] = useState(null);
  const [coiFile, setCoiFile] = useState(null);
  const [coiExpiration, setCoiExpiration] = useState('');
  const [isUploadingW9, setIsUploadingW9] = useState(false);
  const [isUploadingCOI, setIsUploadingCOI] = useState(false);
  
  // Expanded projects state
  const [expandedProjects, setExpandedProjects] = useState({});
  
  // Messaging state
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  
  // Open Bids state
  const [openBids, setOpenBids] = useState([]);
  const [contractorTrades, setContractorTrades] = useState([]);
  const [isLoadingBids, setIsLoadingBids] = useState(false);
  const [showBidForm, setShowBidForm] = useState(false);
  const [selectedBid, setSelectedBid] = useState(null);
  const [selectedBidProject, setSelectedBidProject] = useState(null); // For project tabs in Bids
  const [bidForm, setBidForm] = useState({
    total_amount: '',
    notes: ''
  });
  const [bidFile, setBidFile] = useState(null);
  const [isSubmittingBid, setIsSubmittingBid] = useState(false);
  
  // Password setup state
  const [showPasswordSetup, setShowPasswordSetup] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ password: '', confirm: '' });
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  
  // Company logo state
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => {
    if (token) {
      loadPortalData();
    } else {
      // Safety net: if someone arrives at /contractor-portal with ?email= (old
      // email link from before Feb 2026 fix), redirect them to the correct
      // bid portal route instead of showing a confusing error.
      const email = searchParams.get('email');
      if (email) {
        // Preserve all query params (email, quick_upload, revision, etc.)
        const newPath = `/contractor-bid-portal?${searchParams.toString()}`;
        navigate(newPath, { replace: true });
        return;
      }
      setError('Invalid portal link. Please use the link provided in your email.');
      setIsLoading(false);
    }
  }, [token]);

  const loadPortalData = async () => {
    try {
      setIsLoading(true);
      const res = await apiService.getPortalV2Data(token);
      setPortalData(res.data);
      
      // Auto-expand all projects
      const expanded = {};
      res.data.projects?.forEach(p => { expanded[p.id] = true; });
      setExpandedProjects(expanded);
      
    } catch (err) {
      console.error('Failed to load portal data:', err);
      setError('This portal link is invalid or has expired. Please contact the project owner for a new link.');
    } finally {
      setIsLoading(false);
    }
  };

  // Load messages
  const loadMessages = async () => {
    try {
      setIsLoadingMessages(true);
      const res = await apiService.getContractorPortalMessages(token);
      setMessages(res.data.messages || []);
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  // Load open bids
  const loadOpenBids = async () => {
    try {
      setIsLoadingBids(true);
      const res = await apiService.getContractorOpenBids(token);
      setOpenBids(res.data.open_bids || []);
      setContractorTrades(res.data.contractor_trades || []);
    } catch (err) {
      console.error('Failed to load open bids:', err);
    } finally {
      setIsLoadingBids(false);
    }
  };

  // Load messages and bids when tabs change
  useEffect(() => {
    if (activeTab === 'messages' && token) {
      loadMessages();
    } else if (activeTab === 'bids' && token) {
      loadOpenBids();
    }
  }, [activeTab, token]);

  // Send message handler
  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    try {
      setIsSendingMessage(true);
      await apiService.sendContractorPortalMessage(token, newMessage.trim());
      toast.success('Message sent!');
      setNewMessage('');
      loadMessages();
    } catch (err) {
      console.error('Failed to send message:', err);
      toast.error('Failed to send message');
    } finally {
      setIsSendingMessage(false);
    }
  };

  // Submit bid handler
  const handleSubmitBid = async () => {
    if (!selectedBid || !bidForm.total_amount) {
      toast.error('Please enter a bid amount');
      return;
    }
    
    try {
      setIsSubmittingBid(true);
      
      const formData = new FormData();
      formData.append('bid_request_id', selectedBid.id);
      formData.append('total_amount', bidForm.total_amount);
      if (bidForm.notes) formData.append('notes', bidForm.notes);
      if (bidFile) formData.append('file', bidFile);
      
      await apiService.submitBidFromPortal(token, formData);
      
      toast.success('Bid submitted successfully!');
      setShowBidForm(false);
      setSelectedBid(null);
      setBidForm({ total_amount: '', notes: '' });
      setBidFile(null);
      loadOpenBids();
    } catch (err) {
      console.error('Failed to submit bid:', err);
      toast.error(err.response?.data?.detail || 'Failed to submit bid');
    } finally {
      setIsSubmittingBid(false);
    }
  };

  // Set password handler
  const handleSetPassword = async () => {
    if (passwordForm.password !== passwordForm.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      setIsSettingPassword(true);
      await apiService.contractorSetPassword(token, passwordForm.password);
      toast.success('Password set successfully! You can now login with your email and password.');
      setShowPasswordSetup(false);
      setPasswordForm({ password: '', confirm: '' });
    } catch (err) {
      console.error('Failed to set password:', err);
      toast.error(err.response?.data?.detail || 'Failed to set password');
    } finally {
      setIsSettingPassword(false);
    }
  };

  const handleSubmitInvoice = async () => {
    if (!invoiceForm.phase_id || !invoiceForm.amount || !invoiceForm.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      setIsSubmittingInvoice(true);
      
      const formData = new FormData();
      formData.append('phase_id', invoiceForm.phase_id);
      formData.append('amount', invoiceForm.amount);
      formData.append('description', invoiceForm.description);
      if (invoiceForm.invoice_number) formData.append('invoice_number', invoiceForm.invoice_number);
      if (invoiceForm.invoice_date) formData.append('invoice_date', invoiceForm.invoice_date);
      if (invoiceFile) formData.append('file', invoiceFile);
      
      await apiService.submitPortalInvoice(token, formData);
      
      toast.success('Invoice submitted successfully! The project owner will be notified.');
      setShowInvoiceForm(false);
      setInvoiceForm({
        project_id: '',
        phase_id: '',
        amount: '',
        description: '',
        invoice_number: '',
        invoice_date: new Date().toISOString().split('T')[0]
      });
      setInvoiceFile(null);
      
      // Reload data to get updated invoices
      await loadPortalData();
      
    } catch (err) {
      console.error('Failed to submit invoice:', err);
      toast.error(err.response?.data?.detail || 'Failed to submit invoice');
    } finally {
      setIsSubmittingInvoice(false);
    }
  };

  const openResubmitModal = (invoice) => {
    setResubmittingInvoice(invoice);
    setResubmitForm({
      amount: invoice.amount?.toString() || '',
      description: invoice.description || '',
      invoice_number: invoice.invoice_number || '',
      invoice_date: invoice.invoice_date || new Date().toISOString().split('T')[0]
    });
    setResubmitFile(null);
    setShowResubmitModal(true);
  };

  const handleResubmitInvoice = async () => {
    if (!resubmitForm.amount || !resubmitForm.description) {
      toast.error('Please fill in amount and description');
      return;
    }
    
    try {
      setIsResubmitting(true);
      
      const formData = new FormData();
      formData.append('amount', resubmitForm.amount);
      formData.append('description', resubmitForm.description);
      if (resubmitForm.invoice_number) formData.append('invoice_number', resubmitForm.invoice_number);
      if (resubmitForm.invoice_date) formData.append('invoice_date', resubmitForm.invoice_date);
      if (resubmitFile) formData.append('file', resubmitFile);
      
      await apiService.resubmitInvoice(resubmittingInvoice.id, token, formData);
      
      toast.success('Invoice resubmitted successfully! The project owner will be notified.');
      setShowResubmitModal(false);
      setResubmittingInvoice(null);
      
      // Reload data
      await loadPortalData();
      
    } catch (err) {
      console.error('Failed to resubmit invoice:', err);
      toast.error(err.response?.data?.detail || 'Failed to resubmit invoice');
    } finally {
      setIsResubmitting(false);
    }
  };

  const handleUploadW9 = async () => {
    if (!w9File) {
      toast.error('Please select a W-9 file to upload');
      return;
    }
    
    try {
      setIsUploadingW9(true);
      
      // Convert file to base64 for portal upload
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target.result;
        try {
          await apiService.uploadContractorW9Portal(token, base64, w9File.name);
          toast.success('W-9 uploaded successfully!');
          setW9File(null);
          await loadPortalData();
        } catch (err) {
          console.error('Failed to upload W-9:', err);
          toast.error('Failed to upload W-9');
        } finally {
          setIsUploadingW9(false);
        }
      };
      reader.readAsDataURL(w9File);
    } catch (err) {
      console.error('Failed to upload W-9:', err);
      toast.error('Failed to upload W-9');
      setIsUploadingW9(false);
    }
  };

  const handleUploadCOI = async () => {
    if (!coiFile || !coiExpiration) {
      toast.error('Please select a COI file and expiration date');
      return;
    }

    try {
      setIsUploadingCOI(true);
      // v2.5.12 — switched to /portal/v2/coi (base64 payload, token-authed).
      // The old call was passing 3 args to a 2-arg API stub, which dropped
      // the FormData and resulted in "Failed to upload COI" toasts on prod.
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target.result;
        try {
          await apiService.uploadContractorCOIPortal(token, base64, coiFile.name, coiExpiration);
          toast.success('Certificate of Insurance uploaded successfully!');
          setCoiFile(null);
          setCoiExpiration('');
          // v2.5.12 — refresh portal data, but don't let a transient
          // backend hiccup nuke the success state. A 500 here would otherwise
          // flip the page to "Portal Access Error" and the user would think
          // the upload itself failed.
          try {
            await loadPortalData();
          } catch (refreshErr) {
            console.warn('Post-COI portal refresh failed, but upload succeeded:', refreshErr);
          }
        } catch (err) {
          console.error('Failed to upload COI:', err);
          toast.error(err.response?.data?.detail || 'Failed to upload COI');
        } finally {
          setIsUploadingCOI(false);
        }
      };
      reader.onerror = () => {
        toast.error('Could not read COI file');
        setIsUploadingCOI(false);
      };
      reader.readAsDataURL(coiFile);
    } catch (err) {
      console.error('Failed to upload COI:', err);
      toast.error('Failed to upload COI');
      setIsUploadingCOI(false);
    }
  };

  const handleUploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    try {
      setIsUploadingLogo(true);
      
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target.result;
        
        try {
          await apiService.updateContractorLogo(token, base64);
          toast.success('Company logo updated!');
          await loadPortalData();
        } catch (err) {
          console.error('Failed to upload logo:', err);
          // Show more descriptive error message
          const errorMessage = err.response?.data?.detail || err.message || 'Failed to upload logo';
          toast.error(errorMessage);
        } finally {
          setIsUploadingLogo(false);
        }
      };
      reader.onerror = () => {
        toast.error('Failed to read image file');
        setIsUploadingLogo(false);
      };
      reader.readAsDataURL(file);
      
    } catch (err) {
      console.error('Failed to upload logo:', err);
      toast.error('Failed to upload logo');
      setIsUploadingLogo(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-purple-100 text-purple-800'
    };
    return styles[status] || 'bg-slate-100 text-slate-800';
  };

  const getFilteredPhases = (projectId) => {
    return portalData?.contractor_phases?.filter(p => p.project_id === projectId) || [];
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto text-blue-600 mb-4" />
          <p className="text-slate-600">Loading your portal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Portal Access Error</h2>
              <p className="text-slate-600">{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { contractor, contractor_phases, invoices, schedule, projects } = portalData || {};

  // Tab definitions for sidebar
  const portalTabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'invoices', label: 'Invoices', icon: DollarSign },
    { id: 'bids', label: 'Bids', icon: Gavel },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
    { id: 'schedule', label: 'Schedule', icon: Calendar },
    { id: 'documents', label: 'Documents', icon: FileCheck },
  ];

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Mobile nav drawer overlay (below lg) */}
      {mobileNavOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileNavOpen(false)}
          data-testid="portal-nav-overlay"
        />
      )}
      {/* Sidebar Navigation — fixed on desktop, slide-in hamburger drawer on mobile */}
      <aside className={`fixed inset-y-0 left-0 lg:top-[40px] lg:h-[calc(100vh-40px)] w-64 bg-slate-900 flex flex-col z-50 transform transition-transform duration-300 lg:translate-x-0 lg:z-30 ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Close (mobile drawer only) */}
        <button
          onClick={() => setMobileNavOpen(false)}
          className="lg:hidden absolute top-4 right-3 text-slate-400 hover:text-white z-10"
          data-testid="portal-nav-close"
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>
        {/* Logo/Brand */}
        <div className="p-4 border-b border-slate-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-lg">D</span>
            </div>
            <div className="overflow-hidden">
              <h1 className="font-bold text-lg text-white whitespace-nowrap">DrawReady</h1>
              <p className="text-xs text-slate-400 whitespace-nowrap">Contractor Portal</p>
            </div>
          </div>
        </div>

        {/* Contractor Info Section */}
        <div className="px-4 py-3 border-b border-slate-700 flex-shrink-0">
          <h2 className="font-bold text-white truncate">{contractor?.company || contractor?.name || 'Contractor'}</h2>
          <p className="text-xs text-slate-400 truncate">{contractor?.email || ''}</p>
          {/* Compliance Status */}
          <div className="flex gap-2 mt-2">
            <span className={`text-xs px-2 py-0.5 rounded ${contractor?.w9_received ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
              W-9: {contractor?.w9_received ? 'OK' : 'Req'}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded ${contractor?.coi_received ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
              COI: {contractor?.coi_received ? 'OK' : 'Req'}
            </span>
          </div>
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 py-4 overflow-y-auto min-h-0">
          <ul className="space-y-1 px-3">
            {portalTabs.map(tab => (
              <li key={tab.id}>
                <button
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                    activeTab === tab.id ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="w-5 h-5 flex-shrink-0" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Bottom Section */}
        <div className="border-t border-slate-700 py-4 flex-shrink-0">
          <ul className="space-y-1 px-3">
            <li>
              <button
                onClick={() => setShowPasswordSetup(true)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white transition-all"
              >
                <Lock className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">Set Password</span>
              </button>
            </li>
            <li>
              <button
                onClick={() => window.open('mailto:support@drawready.com', '_blank')}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white transition-all"
              >
                <HelpCircle className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">Help & Support</span>
              </button>
            </li>
          </ul>

          {/* Contractor Avatar with Logo Upload */}
          <div className="mt-4 px-3 pb-2">
            <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-800">
              {/* Logo/Avatar with upload capability */}
              <div 
                className="relative group cursor-pointer"
                onClick={() => logoInputRef.current?.click()}
              >
                {contractor?.company_logo ? (
                  <img 
                    src={contractor.company_logo} 
                    alt="Company logo" 
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-medium">
                      {(contractor?.company || contractor?.name || 'C')[0].toUpperCase()}
                    </span>
                  </div>
                )}
                {/* Camera overlay on hover */}
                <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  {isUploadingLogo ? (
                    <Loader2 className="w-4 h-4 text-white animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4 text-white" />
                  )}
                </div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleUploadLogo}
                  className="hidden"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{contractor?.company || contractor?.name || 'Contractor'}</p>
                <p className="text-xs text-slate-400 truncate">{contractor?.specialty || 'Contractor'}</p>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-1 text-center">Click logo to upload</p>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 min-w-0 lg:ml-64">
        {/* Mobile Header */}
        <div className="lg:hidden bg-white border-b shadow-sm sticky top-0 z-40">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Button
                  onClick={() => setMobileNavOpen(true)}
                  variant="ghost"
                  size="icon"
                  className="text-slate-600 hover:text-slate-900 flex-shrink-0"
                  data-testid="portal-mobile-menu-toggle"
                  aria-label="Open menu"
                >
                  <Menu className="w-6 h-6" />
                </Button>
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">D</span>
                </div>
                <span className="font-bold text-base text-slate-900 truncate">Contractor Portal</span>
              </div>
              <Badge variant={contractor?.w9_received && contractor?.coi_received ? "default" : "destructive"} className="text-xs flex-shrink-0">
                {contractor?.w9_received && contractor?.coi_received ? 'Compliant' : 'Docs Required'}
              </Badge>
            </div>
          </div>
        </div>

        {/* Page Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg">
          <div className="max-w-6xl mx-auto px-4 py-4 sm:py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {/* Company Logo in Header */}
                <div 
                  className="relative group cursor-pointer flex-shrink-0"
                  onClick={() => logoInputRef.current?.click()}
                >
                  {contractor?.company_logo ? (
                    <img 
                      src={contractor.company_logo} 
                      alt="Company logo" 
                      className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-white/30 shadow-lg"
                    />
                  ) : (
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/20 backdrop-blur border-2 border-white/30 flex items-center justify-center">
                      <Camera className="w-6 h-6 sm:w-8 sm:h-8 text-white/70" />
                    </div>
                  )}
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    {isUploadingLogo ? (
                      <Loader2 className="w-6 h-6 text-white animate-spin" />
                    ) : (
                      <Camera className="w-6 h-6 text-white" />
                    )}
                  </div>
                </div>
                
                <div className="min-w-0 flex-1">
                  <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold">Contractor Portal</h1>
                  <p className="text-blue-100 mt-1 text-xs sm:text-sm">{contractor?.company || contractor?.name}</p>
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <Badge variant="secondary" className={`gap-1 ${contractor?.w9_received ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  <FileText className="w-3 h-3" />
                  W-9: {contractor?.w9_received ? 'On File' : 'Required'}
                </Badge>
                <Badge variant="secondary" className={`gap-1 ${contractor?.coi_received ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  <Shield className="w-3 h-3" />
                  COI: {contractor?.coi_received ? 'On File' : 'Required'}
                </Badge>
              </div>
            </div>
          </div>
        </div>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {/* Quick Stats */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500">Active Phases</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-900">{contractor_phases?.length || 0}</div>
                  <p className="text-sm text-slate-500 mt-1">Across {projects?.length || 0} project(s)</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500">Pending Invoices</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-yellow-600">
                    {invoices?.filter(i => i.status === 'pending').length || 0}
                  </div>
                  <p className="text-sm text-slate-500 mt-1">Awaiting approval</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500">Approved Invoices</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-600">
                    ${invoices?.filter(i => i.status === 'approved').reduce((sum, i) => sum + (i.amount || 0), 0).toLocaleString() || '0'}
                  </div>
                  <p className="text-sm text-slate-500 mt-1">Total approved</p>
                </CardContent>
              </Card>
            </div>

            {/* My Assigned Phases */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  My Assigned Phases
                </CardTitle>
                <CardDescription>View all phases you're assigned to across projects</CardDescription>
              </CardHeader>
              <CardContent>
                {projects?.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No phases assigned yet.</p>
                ) : (
                  <div className="space-y-4">
                    {projects?.map(project => (
                      <div key={project.id} className="border rounded-lg overflow-hidden">
                        <button
                          onClick={() => setExpandedProjects(prev => ({ ...prev, [project.id]: !prev[project.id] }))}
                          className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <Building2 className="w-5 h-5 text-slate-400" />
                            <div className="text-left">
                              <h3 className="font-semibold text-slate-900">{project.name}</h3>
                              <p className="text-sm text-slate-500">{project.address}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">{getFilteredPhases(project.id).length} phase(s)</Badge>
                            {expandedProjects[project.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </div>
                        </button>
                        
                        {expandedProjects[project.id] && (
                          <div className="p-4 space-y-3">
                            {getFilteredPhases(project.id).map(phase => (
                              <div key={phase.phase_id} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                                <div>
                                  <h4 className="font-medium text-slate-900">{phase.phase_name}</h4>
                                  <p className="text-sm text-slate-500">
                                    Est: ${phase.estimated_cost?.toLocaleString() || '0'}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge className={getStatusBadge(phase.status)}>{phase.status}</Badge>
                                  <div className="w-24">
                                    <Progress value={phase.progress || 0} className="h-2" />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-600" />
                    My Invoices
                  </CardTitle>
                  <CardDescription>Submit invoices for your completed work</CardDescription>
                </div>
                <Button onClick={() => setShowInvoiceForm(true)} data-testid="submit-invoice-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Submit Invoice
                </Button>
              </CardHeader>
              <CardContent>
                {/* Invoice Submission Form */}
                {showInvoiceForm && (
                  <Card className="mb-6 border-blue-200 bg-blue-50/50">
                    <CardHeader>
                      <CardTitle className="text-lg">Submit New Invoice</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Project *</Label>
                          <Select 
                            value={invoiceForm.project_id} 
                            onValueChange={(val) => {
                              setInvoiceForm(prev => ({ ...prev, project_id: val, phase_id: '' }));
                            }}
                          >
                            <SelectTrigger data-testid="invoice-project-select">
                              <SelectValue placeholder="Select project" />
                            </SelectTrigger>
                            <SelectContent>
                              {projects?.map(p => (
                                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <Label>Phase *</Label>
                          <Select 
                            value={invoiceForm.phase_id} 
                            onValueChange={(val) => setInvoiceForm(prev => ({ ...prev, phase_id: val }))}
                            disabled={!invoiceForm.project_id}
                          >
                            <SelectTrigger data-testid="invoice-phase-select">
                              <SelectValue placeholder="Select phase" />
                            </SelectTrigger>
                            <SelectContent>
                              {getFilteredPhases(invoiceForm.project_id).map(p => (
                                <SelectItem key={p.phase_id} value={p.phase_id}>{p.phase_name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <Label>Amount *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={invoiceForm.amount}
                            onChange={(e) => setInvoiceForm(prev => ({ ...prev, amount: e.target.value }))}
                            data-testid="invoice-amount-input"
                          />
                        </div>
                        <div>
                          <Label>Invoice Number</Label>
                          <Input
                            placeholder="INV-001"
                            value={invoiceForm.invoice_number}
                            onChange={(e) => setInvoiceForm(prev => ({ ...prev, invoice_number: e.target.value }))}
                          />
                        </div>
                        <div>
                          <Label>Invoice Date</Label>
                          <Input
                            type="date"
                            value={invoiceForm.invoice_date}
                            onChange={(e) => setInvoiceForm(prev => ({ ...prev, invoice_date: e.target.value }))}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label>Description *</Label>
                        <Textarea
                          placeholder="Describe the work completed..."
                          value={invoiceForm.description}
                          onChange={(e) => setInvoiceForm(prev => ({ ...prev, description: e.target.value }))}
                          rows={3}
                          data-testid="invoice-description-input"
                        />
                      </div>
                      
                      <div>
                        <Label>Attach Invoice Document (optional)</Label>
                        <Input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)}
                          className="mt-1"
                        />
                      </div>
                      
                      <div className="flex justify-end gap-2 pt-4">
                        <Button variant="outline" onClick={() => setShowInvoiceForm(false)}>Cancel</Button>
                        <Button onClick={handleSubmitInvoice} disabled={isSubmittingInvoice} data-testid="submit-invoice-confirm-btn">
                          {isSubmittingInvoice ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                          ) : (
                            <><Send className="w-4 h-4 mr-2" /> Submit Invoice</>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Invoices List */}
                {invoices?.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No invoices submitted yet.</p>
                    <p className="text-sm">Click "Submit Invoice" to request payment for completed work.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {invoices?.map(invoice => (
                      <div key={invoice.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-slate-900">
                              ${invoice.amount?.toLocaleString()}
                            </h4>
                            <Badge className={getStatusBadge(invoice.status)}>{invoice.status}</Badge>
                          </div>
                          <p className="text-sm text-slate-500 mt-1">{invoice.description}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {invoice.project_name} • {invoice.phase_name} • Submitted {formatDate(invoice.submitted_at)}
                          </p>
                          {invoice.status === 'rejected' && invoice.rejection_reason && (
                            <Alert variant="destructive" className="mt-2">
                              <AlertCircle className="w-4 h-4" />
                              <AlertDescription>
                                <strong>Rejection reason:</strong> {invoice.rejection_reason}
                              </AlertDescription>
                            </Alert>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {invoice.file_url && (
                            <Button variant="outline" size="sm" asChild>
                              <a href={invoice.file_url} target="_blank" rel="noopener noreferrer">
                                <Eye className="w-4 h-4 mr-2" />
                                View
                              </a>
                            </Button>
                          )}
                          {invoice.status === 'rejected' && (
                            <Button 
                              size="sm" 
                              onClick={() => openResubmitModal(invoice)}
                              data-testid={`resubmit-invoice-${invoice.id}`}
                            >
                              <Send className="w-4 h-4 mr-2" />
                              Resubmit
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Open Bids Tab */}
          <TabsContent value="bids">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gavel className="w-5 h-5 text-blue-600" />
                  Open Bid Requests
                </CardTitle>
                <CardDescription>
                  Bid requests matching your trade(s): {contractorTrades.length > 0 ? contractorTrades.join(', ') : 'All trades'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingBids ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  </div>
                ) : openBids.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Gavel className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No open bid requests matching your trade(s).</p>
                    <p className="text-sm mt-2">Check back later for new opportunities.</p>
                  </div>
                ) : (
                  <div className="space-y-5">
                    {/* Project Tabs - Group bids by project */}
                    {(() => {
                      const projectGroups = openBids.reduce((groups, bid) => {
                        // Use project_name as the unique key since project_id may be null
                        const projectKey = bid.project_name || bid.project_id || 'Unknown Project';
                        if (!groups[projectKey]) {
                          groups[projectKey] = {
                            projectKey: projectKey, // Use projectKey as unique identifier
                            projectId: bid.project_id,
                            projectName: bid.project_name,
                            projectAddress: bid.project_address,
                            bids: []
                          };
                        }
                        groups[projectKey].bids.push(bid);
                        return groups;
                      }, {});
                      
                      const projectList = Object.values(projectGroups);
                      // Use projectKey (project name) as the identifier, not projectId
                      const firstProjectKey = projectList.length > 0 ? projectList[0].projectKey : null;
                      const activeProjectKey = selectedBidProject || firstProjectKey;
                      const activeProjectData = projectList.find(p => p.projectKey === activeProjectKey) || projectList[0];
                      
                      return (
                        <>
                          {/* Project Pill Tabs */}
                          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                            {projectList.map((project, index) => {
                              const isActive = project.projectKey === activeProjectKey;
                              return (
                              <button
                                key={project.projectKey}
                                onClick={() => setSelectedBidProject(project.projectKey)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                                  isActive
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                }`}
                              >
                                <Building2 className="w-4 h-4" />
                                {project.projectName}
                                <Badge className={`${
                                  isActive 
                                    ? 'bg-blue-400 text-white' 
                                    : 'bg-slate-300 text-slate-700'
                                }`}>
                                  {project.bids.length}
                                </Badge>
                              </button>
                            );})}
                          </div>

                          {/* Active Project Header */}
                          {activeProjectData && activeProjectData.projectAddress && (
                            <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                              <div className="flex items-center gap-2 text-sm text-slate-600">
                                <Building2 className="w-4 h-4" />
                                <span>{activeProjectData.projectAddress}</span>
                              </div>
                            </div>
                          )}

                          {/* Bids List for Selected Project */}
                          {activeProjectData && (
                            <div className="space-y-3">
                              {activeProjectData.bids.map((bid, bidIndex) => (
                                <div 
                                  key={bid.id} 
                                  className={`p-4 border rounded-lg transition-all ${
                                    bid.already_submitted 
                                      ? 'bg-green-50/50 border-l-4 border-l-green-500' 
                                      : 'hover:bg-slate-50 border-l-4 border-l-transparent hover:border-l-blue-500'
                                  }`}
                                >
                                  <div className="flex items-start gap-4">
                                    {/* Bid number indicator */}
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                                      bid.already_submitted 
                                        ? 'bg-green-100 text-green-700' 
                                        : 'bg-blue-100 text-blue-700'
                                    }`}>
                                      {bidIndex + 1}
                                    </div>
                                    
                                    <div className="flex-1 min-w-0">
                                      <div className="flex flex-wrap items-center gap-2 mb-2">
                                        <h4 className="font-semibold text-slate-900 text-base">{bid.title}</h4>
                                        <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">{bid.trade_type}</Badge>
                                        {bid.already_submitted && (
                                          <Badge className="bg-green-100 text-green-800 gap-1">
                                            <CheckCircle2 className="w-3 h-3" />
                                            Bid Submitted
                                          </Badge>
                                        )}
                                      </div>
                                      
                                      <p className="text-sm text-slate-600 mb-3 line-clamp-2">{bid.scope_of_work}</p>
                                      
                                      <div className="flex flex-wrap items-center gap-4 text-sm">
                                        {bid.due_date && (
                                          <span className={`flex items-center gap-1.5 px-2 py-1 rounded-md ${
                                            new Date(bid.due_date) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                                              ? 'bg-red-50 text-red-700'
                                              : 'bg-slate-100 text-slate-600'
                                          }`}>
                                            <Clock className="w-3.5 h-3.5" />
                                            Due: {formatDate(bid.due_date)}
                                          </span>
                                        )}
                                        {bid.already_submitted && bid.submitted_bid && (
                                          <span className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-green-100 text-green-700 font-medium">
                                            <DollarSign className="w-3.5 h-3.5" />
                                            Your bid: ${bid.submitted_bid.total_amount?.toLocaleString()}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {!bid.already_submitted && (
                                      <Button 
                                        onClick={() => { setSelectedBid(bid); setShowBidForm(true); }}
                                        data-testid={`submit-bid-${bid.id}`}
                                        className="bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0"
                                      >
                                        <Send className="w-4 h-4 mr-2" />
                                        Submit Bid
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                  Messages
                </CardTitle>
                <CardDescription>Communicate with project owners</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Message Input */}
                <div className="flex gap-2 mb-6">
                  <Textarea
                    placeholder="Type your message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    className="flex-1"
                    rows={2}
                    data-testid="message-input"
                  />
                  <Button 
                    onClick={handleSendMessage} 
                    disabled={isSendingMessage || !newMessage.trim()}
                    data-testid="send-message-btn"
                  >
                    {isSendingMessage ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>

                {/* Messages List */}
                {isLoadingMessages ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No messages yet.</p>
                    <p className="text-sm mt-2">Send a message to start a conversation.</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {messages.map((msg, idx) => (
                      <div 
                        key={msg.id || idx} 
                        className={`p-3 rounded-lg ${msg.is_from_me ? 'bg-blue-50 ml-8' : 'bg-slate-100 mr-8'}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-slate-600">
                            {msg.is_from_me ? 'You' : msg.sender_name || 'Project Owner'}
                          </span>
                          <span className="text-xs text-slate-400">
                            {msg.created_at ? new Date(msg.created_at).toLocaleString() : ''}
                          </span>
                        </div>
                        <p className="text-sm text-slate-800">{msg.content}</p>
                        {msg.project_name && (
                          <p className="text-xs text-slate-400 mt-1">Re: {msg.project_name}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Schedule Tab */}
          <TabsContent value="schedule">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-purple-600" />
                  Project Schedule
                </CardTitle>
                <CardDescription>View overall project progress and your assigned phases</CardDescription>
              </CardHeader>
              <CardContent>
                {schedule?.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No schedule data available.</p>
                ) : (
                  <div className="space-y-2">
                    {schedule?.map(phase => (
                      <div 
                        key={phase.id} 
                        className={`flex items-center justify-between p-3 border rounded-lg ${
                          phase.is_assigned_to_me ? 'bg-blue-50 border-blue-200' : 'bg-white'
                        }`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-slate-900">{phase.name}</h4>
                            {phase.is_assigned_to_me && (
                              <Badge variant="default" className="text-xs">Your Phase</Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-500">{phase.project_name}</p>
                          {(phase.start_date || phase.end_date) && (
                            <p className="text-xs text-slate-400 mt-1">
                              {phase.start_date && `Start: ${formatDate(phase.start_date)}`}
                              {phase.start_date && phase.end_date && ' • '}
                              {phase.end_date && `End: ${formatDate(phase.end_date)}`}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={getStatusBadge(phase.status)}>{phase.status}</Badge>
                          <div className="w-24 text-right">
                            <span className="text-sm font-medium">{phase.progress || 0}%</span>
                            <Progress value={phase.progress || 0} className="h-2 mt-1" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <div className="grid gap-6 md:grid-cols-2">
              {/* W-9 Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    W-9 Form
                  </CardTitle>
                  <CardDescription>Tax identification form required for payments</CardDescription>
                </CardHeader>
                <CardContent>
                  {contractor?.w9_received ? (
                    <Alert className="bg-green-50 border-green-200">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <AlertDescription className="text-green-800">
                        W-9 is on file. No action required.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <div className="space-y-4">
                      <Alert variant="destructive">
                        <AlertCircle className="w-4 h-4" />
                        <AlertDescription>
                          W-9 required before payments can be processed.
                        </AlertDescription>
                      </Alert>
                      
                      <div>
                        <Label>Upload W-9 Form (PDF)</Label>
                        <Input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => setW9File(e.target.files?.[0] || null)}
                          className="mt-1"
                        />
                      </div>
                      
                      <Button 
                        onClick={handleUploadW9} 
                        disabled={!w9File || isUploadingW9}
                        className="w-full"
                        data-testid="upload-w9-btn"
                      >
                        {isUploadingW9 ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                        ) : (
                          <><Upload className="w-4 h-4 mr-2" /> Upload W-9</>
                        )}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* COI Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-purple-600" />
                    Certificate of Insurance
                  </CardTitle>
                  <CardDescription>Proof of liability insurance coverage</CardDescription>
                </CardHeader>
                <CardContent>
                  {contractor?.coi_received ? (
                    <div className="space-y-4">
                      <Alert className="bg-green-50 border-green-200">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        <AlertDescription className="text-green-800">
                          COI is on file.
                          {contractor.coi_expiration && (
                            <span className="block mt-1">
                              Expires: {formatDate(contractor.coi_expiration)}
                            </span>
                          )}
                        </AlertDescription>
                      </Alert>
                      
                      <p className="text-sm text-slate-500">
                        Need to update your COI? Upload a new one below.
                      </p>
                      
                      <div className="grid gap-4">
                        <div>
                          <Label>New COI Document (PDF)</Label>
                          <Input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => setCoiFile(e.target.files?.[0] || null)}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Expiration Date</Label>
                          <Input
                            type="date"
                            value={coiExpiration}
                            onChange={(e) => setCoiExpiration(e.target.value)}
                            className="mt-1"
                          />
                        </div>
                        <Button 
                          onClick={handleUploadCOI} 
                          disabled={!coiFile || !coiExpiration || isUploadingCOI}
                          variant="outline"
                        >
                          {isUploadingCOI ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                          ) : (
                            <><Upload className="w-4 h-4 mr-2" /> Update COI</>
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <Alert variant="destructive">
                        <AlertCircle className="w-4 h-4" />
                        <AlertDescription>
                          Certificate of Insurance required before payments can be processed.
                        </AlertDescription>
                      </Alert>
                      
                      <div className="grid gap-4">
                        <div>
                          <Label>Upload COI (PDF)</Label>
                          <Input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => setCoiFile(e.target.files?.[0] || null)}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Expiration Date *</Label>
                          <Input
                            type="date"
                            value={coiExpiration}
                            onChange={(e) => setCoiExpiration(e.target.value)}
                            className="mt-1"
                          />
                        </div>
                        <Button 
                          onClick={handleUploadCOI} 
                          disabled={!coiFile || !coiExpiration || isUploadingCOI}
                          className="w-full"
                          data-testid="upload-coi-btn"
                        >
                          {isUploadingCOI ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                          ) : (
                            <><Upload className="w-4 h-4 mr-2" /> Upload COI</>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Company Logo Section - Full Width Below */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Camera className="w-5 h-5 text-green-600" />
                  Company Logo
                </CardTitle>
                <CardDescription>Upload your company logo to display on bids and documents</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row items-start gap-6">
                  {/* Current Logo Preview */}
                  <div className="flex-shrink-0">
                    <div 
                      className="relative group cursor-pointer"
                      onClick={() => logoInputRef.current?.click()}
                    >
                      {contractor?.company_logo ? (
                        <img 
                          src={contractor.company_logo} 
                          alt="Company logo" 
                          className="w-32 h-32 rounded-xl object-cover border-2 border-slate-200 shadow-sm"
                        />
                      ) : (
                        <div className="w-32 h-32 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center border-2 border-dashed border-blue-300">
                          <div className="text-center">
                            <Camera className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                            <span className="text-xs text-blue-500 font-medium">Add Logo</span>
                          </div>
                        </div>
                      )}
                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        {isUploadingLogo ? (
                          <Loader2 className="w-8 h-8 text-white animate-spin" />
                        ) : (
                          <div className="text-center text-white">
                            <Camera className="w-6 h-6 mx-auto mb-1" />
                            <span className="text-xs">Click to {contractor?.company_logo ? 'change' : 'upload'}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Upload Instructions */}
                  <div className="flex-1 space-y-3">
                    <div>
                      <h4 className="font-medium text-slate-900 mb-1">Logo Guidelines</h4>
                      <ul className="text-sm text-slate-600 space-y-1">
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                          Square or round logos work best
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                          PNG, JPG, or JPEG format
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                          Maximum file size: 5MB
                        </li>
                      </ul>
                    </div>
                    
                    {contractor?.company_logo && (
                      <Alert className="bg-green-50 border-green-200">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        <AlertDescription className="text-green-800">
                          Logo uploaded! It will appear on your bid submissions.
                        </AlertDescription>
                      </Alert>
                    )}
                    
                    <Button 
                      onClick={() => logoInputRef.current?.click()}
                      disabled={isUploadingLogo}
                      variant={contractor?.company_logo ? "outline" : "default"}
                      className="w-full md:w-auto"
                    >
                      {isUploadingLogo ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                      ) : (
                        <><Upload className="w-4 h-4 mr-2" /> {contractor?.company_logo ? 'Change Logo' : 'Upload Logo'}</>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Resubmit Invoice Modal */}
      <Dialog open={showResubmitModal} onOpenChange={setShowResubmitModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-blue-600" />
              Resubmit Invoice
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Alert>
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>
                Update the invoice details below and resubmit for review.
              </AlertDescription>
            </Alert>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={resubmitForm.amount}
                  onChange={(e) => setResubmitForm(prev => ({ ...prev, amount: e.target.value }))}
                  data-testid="resubmit-amount-input"
                />
              </div>
              <div>
                <Label>Invoice Number</Label>
                <Input
                  value={resubmitForm.invoice_number}
                  onChange={(e) => setResubmitForm(prev => ({ ...prev, invoice_number: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <Label>Invoice Date</Label>
              <Input
                type="date"
                value={resubmitForm.invoice_date}
                onChange={(e) => setResubmitForm(prev => ({ ...prev, invoice_date: e.target.value }))}
              />
            </div>
            
            <div>
              <Label>Description *</Label>
              <Textarea
                value={resubmitForm.description}
                onChange={(e) => setResubmitForm(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                data-testid="resubmit-description-input"
              />
            </div>
            
            <div>
              <Label>Attach New Invoice Document (optional)</Label>
              <Input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setResubmitFile(e.target.files?.[0] || null)}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">Leave empty to keep the existing document</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResubmitModal(false)}>Cancel</Button>
            <Button 
              onClick={handleResubmitInvoice} 
              disabled={isResubmitting || !resubmitForm.amount || !resubmitForm.description}
              data-testid="confirm-resubmit-btn"
            >
              {isResubmitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Resubmitting...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Resubmit Invoice</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit Bid Modal */}
      <Dialog open={showBidForm} onOpenChange={setShowBidForm}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gavel className="w-5 h-5 text-blue-600" />
              Submit Bid
            </DialogTitle>
          </DialogHeader>
          {selectedBid && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-semibold text-slate-900">{selectedBid.title}</h4>
                <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">{selectedBid.scope_of_work}</p>
                <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                  <Badge variant="secondary">{selectedBid.trade_type}</Badge>
                  <span>•</span>
                  <span>{selectedBid.project_name}</span>
                </div>
                {selectedBid.scope_attachments?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-200" data-testid="scope-fixtures-section">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
                      Fixtures &amp; specs for this scope
                    </div>
                    <div className="space-y-1.5">
                      {selectedBid.scope_attachments.map((f) => (
                        <a
                          key={f.id || f.file_id}
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${f.file_id}?download=true`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-xs text-blue-700 hover:text-blue-900 hover:underline"
                          data-testid={`scope-fixture-dl-${f.file_id}`}
                        >
                          <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="truncate">{f.filename}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div>
                <Label>Your Bid Amount *</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    className="pl-7"
                    placeholder="0.00"
                    value={bidForm.total_amount}
                    onChange={(e) => setBidForm(prev => ({ ...prev, total_amount: e.target.value }))}
                    data-testid="bid-amount-input"
                  />
                </div>
              </div>
              
              <div>
                <Label>Notes / Comments (optional)</Label>
                <Textarea
                  placeholder="Any additional details about your bid..."
                  value={bidForm.notes}
                  onChange={(e) => setBidForm(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Attach Bid Document (optional)</Label>
                <Input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={(e) => setBidFile(e.target.files?.[0] || null)}
                  className="mt-1"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowBidForm(false); setSelectedBid(null); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitBid} 
              disabled={isSubmittingBid || !bidForm.total_amount}
              data-testid="confirm-bid-btn"
            >
              {isSubmittingBid ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Submit Bid</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Password Modal */}
      <Dialog open={showPasswordSetup} onOpenChange={setShowPasswordSetup}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-blue-600" />
              Set Your Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Set a password to log into your contractor portal anytime without needing the email link.
            </p>
            <div>
              <Label>New Password</Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={passwordForm.password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, password: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Confirm Password</Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={passwordForm.confirm}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm: e.target.value }))}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordSetup(false)}>Cancel</Button>
            <Button 
              onClick={handleSetPassword} 
              disabled={isSettingPassword || !passwordForm.password || !passwordForm.confirm}
            >
              {isSettingPassword ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Setting...</>
              ) : (
                'Set Password'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="max-w-6xl mx-auto px-4 py-6 text-center text-sm text-slate-500">
          <p>Powered by <span className="font-semibold text-blue-600">DrawReady</span></p>
          <p className="mt-1">Questions? Contact the project owner directly.</p>
        </div>
      </footer>
      </div>{/* End of main content area */}
    </div>
  );
}

export default ContractorPortalV2;
